import { loadStripe, PaymentRequest } from '@stripe/stripe-js';
import { trackPurchaseConversion } from '@/utils/analytics';

let paymentRequest: PaymentRequest | null = null;
let googlePayReady = false;
let initialized = false;

export async function ensureGooglePay(publishableKey: string, country: string, currency: string): Promise<boolean> {
  if (initialized) return googlePayReady;
  
  try {
    console.log('[GOOGLE-PAY] Initializing with currency:', { country, currency });
    
    const stripe = await loadStripe(publishableKey);
    if (!stripe) return false;

    // Validate currency is supported by Stripe
    const validCurrencies = ['usd', 'eur', 'gbp'];
    const normalizedCurrency = currency.toLowerCase();
    if (!validCurrencies.includes(normalizedCurrency)) {
      console.error('[GOOGLE-PAY] Invalid currency:', currency, 'defaulting to usd');
      return false;
    }

    paymentRequest = stripe.paymentRequest({
      country: country.toUpperCase(),
      currency: normalizedCurrency,
      total: { label: 'BUXBOX Subscription', amount: 1 },
      requestPayerEmail: true,
    });

    const result = await paymentRequest.canMakePayment();
    console.log('[GOOGLE-PAY] Can make payment:', result, 'for currency:', normalizedCurrency);
    
    googlePayReady = !!result?.googlePay;
    initialized = true;
    
    return googlePayReady;
  } catch (error) {
    console.error('[GOOGLE-PAY] Init error:', error);
    initialized = true;
    return false;
  }
}

export async function tryGooglePay(
  publishableKey: string,
  userEmail: string,
  tier: 'weekly' | 'monthly' | 'annual',
  amount: number,
  currencyCode: string,
  hasHadSubscription: boolean,
  monthlyPrice: number,
  createSetupIntent: () => Promise<{ client_secret: string }>,
  startSubscription: (paymentMethod: string) => Promise<{ subscription_id?: string } | void>
): Promise<boolean> {
  if (!paymentRequest || !googlePayReady) {
    console.log('[GOOGLE-PAY] Not ready:', { paymentRequest: !!paymentRequest, googlePayReady });
    return false;
  }

  // Update the payment request with the correct amount and details
  const currencySymbol = currencyCode.toUpperCase() === 'GBP' ? '£' : currencyCode.toUpperCase() === 'EUR' ? '€' : '$';
  
  console.log('[GOOGLE-PAY] Showing payment sheet:', { 
    currency: currencyCode, 
    amount: monthlyPrice, 
    hasHadSubscription 
  });
  const trialEndDate = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000);
  const formattedDate = trialEndDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  
  const displayItems = hasHadSubscription 
    ? [{ label: 'BUXBOX Monthly Subscription', amount: Math.round(monthlyPrice * 100) }]
    : [
        { label: '7-Day Free Trial', amount: 0 },
        { label: `Billing starts ${formattedDate}`, amount: 0 },
        { label: `Monthly subscription: ${currencySymbol}${monthlyPrice.toFixed(2)}`, amount: 0 }
      ];

  paymentRequest.update({
    total: {
      label: hasHadSubscription ? 'Due today' : 'Card authorization (no charge today)',
      amount: hasHadSubscription ? Math.round(monthlyPrice * 100) : 0,
    },
    displayItems,
  });

  return new Promise<boolean>((resolve, reject) => {
    let completed = false;

    const cleanup = () => {
      if (paymentRequest) {
        paymentRequest.off('paymentmethod', handlePaymentMethod);
        paymentRequest.off('cancel', handleCancel);
      }
    };

    const handlePaymentMethod = async (e: any) => {
      if (completed) return;
      completed = true;
      
      try {
        console.log('[GOOGLE-PAY] Payment method received');
        
        const { client_secret } = await createSetupIntent();
        const stripe = await loadStripe(publishableKey);
        
        if (!stripe) {
          e.complete('fail');
          cleanup();
          return reject(new Error('PAYMENT_FAILED'));
        }

        const { error, setupIntent } = await stripe.confirmCardSetup(client_secret, {
          payment_method: e.paymentMethod.id,
        });

        if (error) {
          console.error('[GOOGLE-PAY] Confirm error:', error);
          e.complete('fail');
          cleanup();
          return reject(new Error('PAYMENT_FAILED'));
        }

        const result = await startSubscription(setupIntent!.payment_method as string);
        
        // CRITICAL: Only track conversion if subscription was actually created
        const subscriptionId = result && typeof result === 'object' ? result.subscription_id : undefined;
        if (!subscriptionId) {
          console.error('[GOOGLE-PAY] Subscription failed - no subscription_id returned');
          e.complete('fail');
          cleanup();
          return reject(new Error('SUBSCRIPTION_FAILED'));
        }
        
        // Track purchase conversion with verified subscription_id
        trackPurchaseConversion({
          transaction_id: subscriptionId,
          value: hasHadSubscription ? monthlyPrice : 0,
          currency: currencyCode.toLowerCase(),
          tier: tier,
          payment_method: 'google_pay',
          is_trial: !hasHadSubscription
        });
        
        e.complete('success');
        cleanup();
        resolve(true);
      } catch (error) {
        console.error('[GOOGLE-PAY] Error:', error);
        e.complete('fail');
        cleanup();
        reject(new Error('PAYMENT_FAILED'));
      }
    };

    const handleCancel = () => {
      if (completed) return;
      completed = true;
      console.log('[GOOGLE-PAY] User cancelled payment sheet');
      cleanup();
      reject(new Error('USER_CANCELLED'));
    };

    paymentRequest!.on('paymentmethod', handlePaymentMethod);
    paymentRequest!.on('cancel', handleCancel);
    
    // Timeout after 5 minutes if no response
    setTimeout(() => {
      if (!completed) {
        completed = true;
        console.log('[GOOGLE-PAY] Payment timeout');
        cleanup();
        reject(new Error('USER_CANCELLED'));
      }
    }, 300000);
    
    try {
      paymentRequest!.show();
    } catch (error) {
      if (completed) return;
      completed = true;
      console.error('[GOOGLE-PAY] Show error:', error);
      cleanup();
      reject(new Error('PAYMENT_FAILED'));
    }
  });
}
